import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  BillStatusEnum,
  ClearableHoldTypes,
  GetBillsDocument,
  useBulkUpdateBillsMutation,
  useClearHoldsMutation,
} from '../../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import {
  BillTypeDataList,
  ReleasableHoldReasons,
} from '../../bill-grid-components/bill-management-types';
import { doesClientInsurerChangeMatch } from '../../utils';
import { BillPanelProps } from '../panel-types';
import { ClientInsurerSelect } from './client-insurer-select';
import { ContractSelect } from './contract-select';
import { releaseInvalid } from './release-validation';
import { SelectedBillsGrid } from './selected-grid';

export const ReleasePanel = ({
  selectedRows,
  clients,
  insurers,
  onClearSelections,
}: BillPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [panelSelectedRows, setPanelSelectedRows] =
    useState<BillTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');
  const [releasedToClientInsurerId, setReleasedToClientInsurerId] = useState<
    number | undefined
  >();
  const [releasedToContractId, setReleasedToContractId] = useState<
    number | undefined
  >();

  const [clearHolds, { loading: clearHoldsLoading }] = useClearHoldsMutation();
  const [bulkUpdateBills, { loading: bulkUpdateLoading }] =
    useBulkUpdateBillsMutation();

  const hasSameHoldType = panelSelectedRows.every(
    row =>
      row.holdDetails?.holdType === panelSelectedRows[0].holdDetails?.holdType,
  );
  const selectedHoldType =
    hasSameHoldType && panelSelectedRows.length > 0
      ? panelSelectedRows[0].holdDetails?.holdType
      : 'invalid';
  const isCarrierChange =
    selectedHoldType === ReleasableHoldReasons.CARRIER_CHANGE;
  const isClientChange =
    selectedHoldType === ReleasableHoldReasons.CLIENT_CHANGE;
  const clientInsurerChangesMatch =
    doesClientInsurerChangeMatch(panelSelectedRows);

  const handleClientInsurerSelect = (value: number): void => {
    setReleasedToClientInsurerId(value);
    setReleasedToContractId(undefined);
  };

  const handleRelease = async (): Promise<void> => {
    try {
      if (
        !releasedToClientInsurerId &&
        selectedHoldType === ReleasableHoldReasons.MANUAL_HOLD
      ) {
        const result = await clearHolds({
          variables: {
            clearHoldsInput: {
              billIds: panelSelectedRows.map(row => row.billId),
              holdTypes: [ClearableHoldTypes.ManualHold],
            },
          },
          refetchQueries: [GetBillsDocument],
        });
        setSnackbarState(
          result.data?.clearHolds?.details ?? 'Successfully released holds',
          'success',
          5000,
        );
      } else if (!releasedToClientInsurerId) {
        // releaseInvalid() should prevent reaching here, but guard defensively
        return;
      } else {
        const result = await bulkUpdateBills({
          variables: {
            bulkUpdateBillsInput: {
              billIds: panelSelectedRows.map(row => row.billId),
              statusChange: BillStatusEnum.SubmittableWithBypass,
              note,
              clientInsurerId: releasedToClientInsurerId,
              ...((isCarrierChange || isClientChange) && releasedToContractId
                ? { contractId: releasedToContractId }
                : {}),
            },
          },
          refetchQueries: [GetBillsDocument],
        });
        setSnackbarState(
          result.data?.bulkUpdateBills?.details ?? 'Successfully updated bills',
          'success',
          5000,
        );
      }
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to release with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (selectedGridRows: BillTypeDataList): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.RELEASE}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.BILL}
      />,
    );
  };

  const pickerInputs = (
    <>
      <ClientInsurerSelect
        disabled={
          !hasSameHoldType ||
          !clientInsurerChangesMatch ||
          selectedHoldType === ReleasableHoldReasons.MANUAL_HOLD
        }
        selectedRows={panelSelectedRows}
        onSelectCallback={handleClientInsurerSelect}
      />
      {(isCarrierChange || isClientChange) && (
        <ContractSelect
          key={releasedToClientInsurerId}
          clientsInsurerId={releasedToClientInsurerId}
          selectedRows={panelSelectedRows}
          onSelectCallback={setReleasedToContractId}
        />
      )}
    </>
  );

  return (
    <PanelLayout
      selectedGrid={
        <SelectedBillsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
          clients={clients}
          insurers={insurers}
        />
      }
      alert={
        <PanelAlert
          drawerMode="release"
          hasSameHoldType={hasSameHoldType}
          clientInsurerChangesMatch={clientInsurerChangesMatch}
        />
      }
      additionalInputs={pickerInputs}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={clearHoldsLoading || bulkUpdateLoading}
      isInvalid={releaseInvalid(
        note,
        selectedHoldType,
        releasedToClientInsurerId,
        releasedToContractId,
      )}
      handleSubmit={handleRelease}
      actionLabel="Release"
    />
  );
};
